import { ChangeEvent, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Close, EmailOutlined } from '@mui/icons-material';
import { QButton, QIconButton, QModal, QModalActions, QModalContent, QTextField, QTypography } from '@kw/quokka-ui';
import { useSnackbar } from 'notistack';
import { MutatorOptions } from 'swr';
import { validateEmail } from '../../../utils/emailValidation';
import { sendHttpRequest } from '../../../utils/network.service';
import { OrganizationSettingCard } from './OrganizationSettingCard';

export const DEFAULT_SUPPORT_EMAIL = 'support@quokka.io';

interface CustomSupportEmailCardProps {
  isAdmin: boolean;
  currentSupportEmail?: string;
  lastUpdated?: string;
  mutate: (data?: unknown, opts?: boolean | MutatorOptions<unknown>) => Promise<unknown>;
}

export const CustomSupportEmailCard = ({ isAdmin, currentSupportEmail, lastUpdated, mutate }: CustomSupportEmailCardProps) => {
  const organizationSupportEmail = currentSupportEmail?.trim() || DEFAULT_SUPPORT_EMAIL;
  const [savedSupportEmail, setSavedSupportEmail] = useState(organizationSupportEmail);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [pendingSupportEmail, setPendingSupportEmail] = useState(organizationSupportEmail);
  const [emailTouched, setEmailTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localLastUpdated, setLocalLastUpdated] = useState<string | Date | undefined>(lastUpdated);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    setSavedSupportEmail(organizationSupportEmail);
  }, [organizationSupportEmail]);

  useEffect(() => {
    if (!isEditorOpen) setPendingSupportEmail(savedSupportEmail);
  }, [isEditorOpen, savedSupportEmail]);

  useEffect(() => {
    if (lastUpdated) setLocalLastUpdated(lastUpdated);
  }, [lastUpdated]);

  const trimmedSupportEmail = pendingSupportEmail.trim();
  const isEmailValid = validateEmail(trimmedSupportEmail);
  const isSaveDisabled = isSaving || !isEmailValid || trimmedSupportEmail === savedSupportEmail;

  const closeEditor = () => {
    if (isSaving) return;

    setIsEditorOpen(false);
    setPendingSupportEmail(savedSupportEmail);
    setEmailTouched(false);
  };

  const openEditor = () => {
    setPendingSupportEmail(savedSupportEmail);
    setEmailTouched(false);
    setIsEditorOpen(true);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPendingSupportEmail(event.target.value);
    setEmailTouched(true);
  };

  const updateSupportEmail = async () => {
    setEmailTouched(true);
    if (isSaveDisabled) return;

    setIsSaving(true);
    const result = await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/`,
      method: 'PUT',
      body: { supportEmail: trimmedSupportEmail },
      successMessage: `Support email set to ${trimmedSupportEmail}.`,
      errorMessage: 'Error updating support email.',
      enqueueSnackbar,
      successOptions: {
        action: snackbarId => (
          <DismissButton aria-label="Dismiss notification" size="small" onClick={() => closeSnackbar(snackbarId)}>
            <Close fontSize="small" />
          </DismissButton>
        ),
      },
    });
    setIsSaving(false);

    if (result.error) return;

    setSavedSupportEmail(trimmedSupportEmail);
    setPendingSupportEmail(trimmedSupportEmail);
    setLocalLastUpdated(new Date());
    setIsEditorOpen(false);
    setEmailTouched(false);
    await mutate();
  };

  return (
    <>
      <OrganizationSettingCard
        title="Support Email"
        description="Enter the email address where Q-scout mobile app user support requests should be sent."
        icon={<EmailOutlined />}
        valueLabel="Email"
        value={savedSupportEmail}
        lastUpdated={localLastUpdated}
        onEdit={isAdmin ? openEditor : undefined}
      />

      <QModal
        open={isEditorOpen}
        onClose={closeEditor}
        aria-labelledby="edit-support-email-title"
        maxWidth={false}
        sx={{
          '& .MuiDivider-root': {
            display: 'none',
          },
        }}
        PaperProps={{
          sx: {
            width: '536px',
            minWidth: 0,
            maxWidth: 'calc(100vw - 34px)',
            borderRadius: '12px',
            overflow: 'hidden',
          },
        }}
        title={
          <ModalHeader>
            <ModalTitle id="edit-support-email-title" variant="h3Medium">
              Edit support email
            </ModalTitle>
          </ModalHeader>
        }
        content={
          <ModalContent>
            <ModalDescription variant="bodyRegular">
              In-app support requests from Q-scout mobile app users enrolled in your organization are sent to this address.
            </ModalDescription>
            <FieldLabel htmlFor="support-email-input">Email</FieldLabel>
            <EmailField
              id="support-email-input"
              inputProps={{ 'aria-label': 'Email' }}
              value={pendingSupportEmail}
              onChange={handleEmailChange}
              placeholder="support@example.com"
              type="email"
              autoFocus
              error={emailTouched && !isEmailValid}
              fullWidth
              disabled={isSaving}
            />
            {emailTouched && !isEmailValid && <ValidationMessage role="alert">Enter a valid email address.</ValidationMessage>}
          </ModalContent>
        }
        actions={
          <ModalActions>
            <QButton variant="outlined" onClick={closeEditor} disabled={isSaving}>
              Cancel
            </QButton>
            <QButton variant="filled" onClick={updateSupportEmail} disabled={isSaveDisabled}>
              {isSaving ? 'Saving…' : 'Save'}
            </QButton>
          </ModalActions>
        }
      />
    </>
  );
};

const ModalTitle = styled(QTypography)`
  display: block;
  font-size: 18px;
  line-height: 25px;
  font-weight: 700;
`;

const ModalHeader = styled('div')`
  padding: 25px 27px 0;
`;

const ModalContent = styled(QModalContent)`
  padding: 8px 27px 0;
`;

const ModalDescription = styled(QTypography)`
  display: block;
  color: #52666d;
  font-size: 14px;
  line-height: 22px;
`;

const FieldLabel = styled('label')`
  display: block;
  margin: 19px 0 8px;
  color: #66777d;
  font-size: 11px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const EmailField = styled(QTextField)`
  .MuiOutlinedInput-root {
    border-radius: 8px;
  }
`;

const ValidationMessage = styled('div')`
  margin-top: 8px;
  color: #df2e21;
  font-size: 13px;
  line-height: 18px;
`;

const ModalActions = styled(QModalActions)`
  gap: 10px;
  padding: 23px 27px 22px;

  button {
    min-width: 74px;
  }
`;

const DismissButton = styled(QIconButton)`
  color: #ffffff;
`;
